import React from 'react';
import BaseEChart from './BaseEChart';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS, CHART_FONT_FAMILY } from './chartConstants';
import { formatNumber } from '@utils/format';
import {
  computeTargetPriceAxis,
  getTargetPriceAxisFractionDigits,
} from './targetPriceAxis';
import {
  TARGET_PRICE_FORECAST_SLOT_COUNT,
  TARGET_PRICE_X_AXIS_LABEL,
  buildTargetPriceCategoryData,
} from './targetPriceXAxis';
import type { ChartOption } from '@app-types/chart';
import type { AnalystRatingHistoryPoint, TargetPrice } from '@app-types/performance';

export interface TargetPriceChartProps {
  targetPrice: TargetPrice;
  ratingHistory?: AnalystRatingHistoryPoint[];
  height?: number;
}

type TargetKind = 'highest' | 'average' | 'lowest';

const TARGET_LABELS: Record<TargetKind, string> = {
  highest: '最高値',
  average: '平均',
  lowest: '最安値',
};

const TARGET_COLORS: Record<TargetKind, string> = {
  highest: CHART_COLORS.seriesPrimary,
  average: '#5a5a5a',
  lowest: '#3366ff',
};

/** work_items_78: visible history series name; also its BA-confirmed tooltip row label. */
const HISTORY_SERIES_NAME = '株価';
/** work_items_78: tooltip metadata row label rendered above 株価. */
const RATING_ROW_LABEL = '平均レーティング';

const TARGET_CHART_HEIGHT = 175;
const TARGET_LABEL_WIDTH = 93;
const TARGET_LABEL_HEIGHT = 38;
const TARGET_LABEL_LINE_HEIGHT = 17;
const TARGET_LABEL_RIGHT = 0;
const TARGET_LABEL_GAP = 14;
const TARGET_LABEL_VERTICAL_GAP = 8;
const TARGET_GRID_LEFT_MIN = 40;
const TARGET_GRID_TOP = 8;
const TARGET_GRID_BOTTOM = 25;
const TARGET_AXIS_LABEL_MARGIN = 8;
const TARGET_AXIS_LABEL_CHAR_WIDTH = 7;
const TARGET_AXIS_LABEL_SAFE_PADDING = 4;
/** Figma 3632:33786 draws every data marker as an 8x8 circle. */
const TARGET_MARKER_SIZE = 8;

interface AxisTooltipParam {
  seriesName?: string;
  data?: number | null;
  color?: string;
  dataIndex?: number;
  axisValueLabel?: string;
  axisValue?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });

const tooltipRow = (name: string, value: string, color?: string): string => {
  const marker = color
    ? `<span class="esmart-chart-tooltip__marker" style="background:${escapeHtml(
        color,
      )}"></span>`
    : '';
  return `<div class="esmart-chart-tooltip__row">${marker}<span class="esmart-chart-tooltip__name">${escapeHtml(
    name,
  )}</span><span class="esmart-chart-tooltip__value">${value}</span></div>`;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const createForecastData = (
  length: number,
  currentIndex: number,
  endIndex: number,
  current: number | null,
  target: number | null,
) => {
  const data = Array<number | null>(length).fill(null);
  if (currentIndex >= 0 && isFiniteNumber(current)) {
    data[currentIndex] = current;
  }
  if (isFiniteNumber(target)) {
    data[endIndex] = target;
  }
  return data;
};

const computeGridLeft = (
  axisMin: number,
  axisMax: number,
  axisInterval: number,
  fractionDigits: number,
): number => {
  if (!Number.isFinite(axisInterval) || axisInterval <= 0) {
    return TARGET_GRID_LEFT_MIN;
  }

  const tickCount = Math.max(0, Math.floor((axisMax - axisMin) / axisInterval));
  const labels = new Set<string>();
  for (let index = 0; index <= tickCount; index += 1) {
    labels.add(formatNumber(axisMin + index * axisInterval, fractionDigits));
  }
  labels.add(formatNumber(axisMax, fractionDigits));

  const maxLabelLength = Math.max(...Array.from(labels).map((label) => label.length));
  const requiredLeft =
    maxLabelLength * TARGET_AXIS_LABEL_CHAR_WIDTH +
    TARGET_AXIS_LABEL_MARGIN +
    TARGET_AXIS_LABEL_SAFE_PADDING;

  return Math.max(TARGET_GRID_LEFT_MIN, Math.ceil(requiredLeft));
};

/**
 * Target price chart (Figma 3333:13056 / 3632:33710).
 *
 * work_items_78: the single visible history line represents price
 * (`breakdown[].price`) on the primary price y-axis. Mean rating history is no
 * longer a visible series; it is tooltip metadata only, so there is no hidden
 * secondary rating axis. The three forecast lines (high/average/low) still draw
 * from the current price to their target on the same primary axis.
 */
/**
 * Pure builder for the Target Price ECharts option. Exported so tests can lock
 * the chart contract (visible series, single price axis, deterministic tooltip
 * row order) without importing `echarts-for-react`.
 */
export const buildTargetPriceChartOption = (
  targetPrice: TargetPrice,
  ratingHistory?: AnalystRatingHistoryPoint[],
  height: number = TARGET_CHART_HEIGHT,
): ChartOption => {
  const { min: axisMin, max: axisMax, interval: axisInterval } =
    computeTargetPriceAxis(targetPrice);
  const axisFractionDigits = getTargetPriceAxisFractionDigits(axisInterval);
  const gridLeft = computeGridLeft(axisMin, axisMax, axisInterval, axisFractionDigits);
  const history = targetPrice.history;
  const currentIndex = Math.max(history.length - 1, 0);
  const forecastSlotCount = TARGET_PRICE_FORECAST_SLOT_COUNT;
  const { xData, historyData } = buildTargetPriceCategoryData(
    history,
    forecastSlotCount,
  );
  const endIndex = xData.length - 1;
  const historyTooltipLabels = history.map((point) => point.tooltipLabel ?? point.label ?? '');
  // Rating metadata keyed by history data index for the tooltip. The rating is
  // never rendered as a series; it is looked up by `dataIndex` so the tooltip
  // row order stays deterministic regardless of ECharts series ordering.
  const ratingByIndex = Array<number | null>(xData.length).fill(null);
  ratingHistory?.slice(0, history.length).forEach((point, index) => {
    ratingByIndex[index] = point.value;
  });

  const getPriceLabelDesiredTop = (value: number | null, fallbackIndex: number) => {
    if (!isFiniteNumber(value)) {
      return fallbackIndex * (TARGET_LABEL_HEIGHT + TARGET_LABEL_VERTICAL_GAP);
    }

    const plotHeight = height - TARGET_GRID_TOP - TARGET_GRID_BOTTOM;
    const ratio = (axisMax - value) / (axisMax - axisMin);
    const pointY = TARGET_GRID_TOP + ratio * plotHeight;
    return clamp(pointY - TARGET_LABEL_HEIGHT / 2, 0, height - TARGET_LABEL_HEIGHT);
  };

  const priceLabelLayout = ([
    { kind: 'highest' as const, value: targetPrice.highest },
    { kind: 'average' as const, value: targetPrice.average },
    { kind: 'lowest' as const, value: targetPrice.lowest },
  ])
    .map((item, index) => ({
      ...item,
      order: index,
      top: getPriceLabelDesiredTop(item.value, index),
    }))
    .sort((a, b) => {
      const aValue = isFiniteNumber(a.value) ? a.value : Number.NEGATIVE_INFINITY - a.order;
      const bValue = isFiniteNumber(b.value) ? b.value : Number.NEGATIVE_INFINITY - b.order;
      return bValue - aValue;
    });

  const minTopDistance = TARGET_LABEL_HEIGHT + TARGET_LABEL_VERTICAL_GAP;
  priceLabelLayout.forEach((item, index) => {
    if (index === 0) {
      return;
    }
    item.top = Math.max(item.top, priceLabelLayout[index - 1].top + minTopDistance);
  });
  const bottomOverflow =
    priceLabelLayout[priceLabelLayout.length - 1].top +
    TARGET_LABEL_HEIGHT -
    height;
  if (bottomOverflow > 0) {
    priceLabelLayout.forEach((item) => {
      item.top -= bottomOverflow;
    });
  }
  const topOverflow = -priceLabelLayout[0].top;
  if (topOverflow > 0) {
    priceLabelLayout.forEach((item) => {
      item.top += topOverflow;
    });
  }

  const priceBlurbGraphics = priceLabelLayout.map(({ kind, value, top }) => {
    const color = TARGET_COLORS[kind];
    return {
      type: 'group',
      right: TARGET_LABEL_RIGHT,
      top,
      z: 20,
      silent: true,
      children: [
        {
          type: 'rect',
          shape: {
            width: TARGET_LABEL_WIDTH,
            height: TARGET_LABEL_HEIGHT,
            r: 4,
          },
          style: {
            fill: '#ffffff',
            stroke: color,
            lineWidth: 1,
          },
        },
        {
          type: 'text',
          style: {
            text: TARGET_LABELS[kind],
            x: TARGET_LABEL_WIDTH / 2,
            y: 2 + TARGET_LABEL_LINE_HEIGHT / 2,
            width: TARGET_LABEL_WIDTH,
            height: TARGET_LABEL_LINE_HEIGHT,
            align: 'center',
            verticalAlign: 'middle',
            fill: color,
            fontFamily: CHART_FONT_FAMILY,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: TARGET_LABEL_LINE_HEIGHT,
            letterSpacing: -0.4,
          },
        },
        {
          type: 'text',
          style: {
            text: formatNumber(value, 2),
            x: TARGET_LABEL_WIDTH / 2,
            y: 19 + TARGET_LABEL_LINE_HEIGHT / 2,
            width: TARGET_LABEL_WIDTH,
            height: TARGET_LABEL_LINE_HEIGHT,
            align: 'center',
            verticalAlign: 'middle',
            fill: '#484848',
            fontFamily: CHART_FONT_FAMILY,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: TARGET_LABEL_LINE_HEIGHT,
            letterSpacing: -0.4,
          },
        },
      ],
    };
  });

  const createForecastSeries = (kind: TargetKind, value: number | null) => {
    const color = TARGET_COLORS[kind];
    return {
      name: TARGET_LABELS[kind],
      type: 'line' as const,
      yAxisIndex: 0,
      data: createForecastData(
        xData.length,
        currentIndex,
        endIndex,
        targetPrice.current,
        value,
      ),
      connectNulls: true,
      symbol: (_value: unknown, params: { dataIndex: number }) =>
        params.dataIndex === endIndex ? 'circle' : 'none',
      symbolSize: (_value: unknown, params: { dataIndex: number }) =>
        params.dataIndex === endIndex ? TARGET_MARKER_SIZE : 0,
      itemStyle: {
        color,
        borderColor: color,
      },
      lineStyle: {
        color,
        width: 2,
        // Figma 3632:33710: all three forecast lines are dotted, including 平均.
        type: 'dotted' as const,
      },
      label: { show: false },
      emphasis: { disabled: true },
    };
  };

  const priceFormatter = (value: number | null | undefined) =>
    formatNumber(value, axisFractionDigits);

  const option: ChartOption = {
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      confine: true,
      className: 'esmart-chart-tooltip esmart-chart-tooltip--target-price',
      formatter: (params: unknown) => {
        const items = Array.isArray(params)
          ? (params as AxisTooltipParam[])
          : [params as AxisTooltipParam];
        if (items.length === 0) {
          return '';
        }
        const tooltipDataIndex = items.find((item) => isFiniteNumber(item.dataIndex))?.dataIndex;
        const historyTooltipLabel = isFiniteNumber(tooltipDataIndex) &&
          tooltipDataIndex >= 0 &&
          tooltipDataIndex < history.length
          ? historyTooltipLabels[tooltipDataIndex]
          : '';
        // work_items_78 follow-up: the x-axis intentionally stays sparse, but
        // every 12MA..CURR history tooltip must show its full YYYY/MM month.
        const periodLabel = historyTooltipLabel ||
          items[0]?.axisValueLabel ||
          items[0]?.axisValue ||
          '';
        const forecastNames = new Set<string>([
          TARGET_LABELS.highest,
          TARGET_LABELS.average,
          TARGET_LABELS.lowest,
        ]);
        const rowParts: string[] = [];
        items.forEach((item) => {
          const value = item.data;
          if (!isFiniteNumber(value)) {
            return;
          }
          const seriesName = item.seriesName ?? '';
          if (forecastNames.has(seriesName)) {
            // Forecast series also carry the current price at currentIndex to
            // draw the line; only surface their target value at the end slot.
            if (item.dataIndex !== endIndex) {
              return;
            }
            rowParts.push(tooltipRow(seriesName, priceFormatter(value), item.color));
            return;
          }
          if (seriesName === HISTORY_SERIES_NAME) {
            // work_items_78: build the two history rows explicitly so the order
            // never depends on ECharts series ordering. 平均レーティング is
            // always first (four decimals, e.g. 4.1176), 株価 second (grouped,
            // e.g. 5,524). A null rating omits only the rating row.
            const rating = isFiniteNumber(item.dataIndex)
              ? ratingByIndex[item.dataIndex]
              : null;
            if (isFiniteNumber(rating)) {
              // meanRating is tooltip-only metadata, so ECharts does not
              // provide it with a separate series marker. Reuse the visible
              // price-history series color so both BA-confirmed rows keep the
              // same marker/alignment.
              rowParts.push(
                tooltipRow(RATING_ROW_LABEL, formatNumber(rating, 4), item.color),
              );
            }
            rowParts.push(tooltipRow(HISTORY_SERIES_NAME, priceFormatter(value), item.color));
          }
        });
        const rows = rowParts.join('');
        if (rows === '') {
          return '';
        }
        const title = periodLabel
          ? `<div class="esmart-chart-tooltip__title">${escapeHtml(periodLabel)}</div>`
          : '';
        return `${title}${rows}`;
      },
    },
    grid: {
      left: gridLeft,
      right: TARGET_LABEL_WIDTH + TARGET_LABEL_GAP,
      top: TARGET_GRID_TOP,
      bottom: TARGET_GRID_BOTTOM,
      containLabel: false,
    },
    graphic: {
      elements: priceBlurbGraphics,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xData,
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: {
        show: true,
        // Figma 3632:33710 draws 9 vertical gridlines across the plot, i.e.
        // every 2nd category of the 13 history + 5 forecast slots.
        interval: 1,
        lineStyle: { color: CHART_COLORS.gridSubtle, width: 1 },
      },
      axisLabel: TARGET_PRICE_X_AXIS_LABEL,
    },
    yAxis: [
      {
        type: 'value',
        min: axisMin,
        max: axisMax,
        interval: axisInterval,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: CHART_COLORS.gridSubtle, width: 1 },
        },
        axisLabel: {
          ...CHART_AXIS_LABEL_STYLE,
          formatter: (value: number) => formatNumber(value, axisFractionDigits),
        },
      },
    ],
    series: [
      {
        name: HISTORY_SERIES_NAME,
        type: 'line',
        yAxisIndex: 0,
        data: historyData,
        connectNulls: false,
        symbol: 'circle',
        symbolSize: TARGET_MARKER_SIZE,
        itemStyle: {
          color: CHART_COLORS.seriesCompare,
          borderColor: CHART_COLORS.seriesCompare,
        },
        lineStyle: {
          color: CHART_COLORS.seriesCompare,
          width: 2,
        },
        label: { show: false },
        markLine: {
          silent: true,
          symbol: 'none',
          label: { show: false },
          data: [{ xAxis: currentIndex }],
          lineStyle: {
            color: '#999999',
            width: 1,
            type: 'solid',
          },
        },
        emphasis: { disabled: true },
      },
      createForecastSeries('highest', targetPrice.highest),
      createForecastSeries('average', targetPrice.average),
      createForecastSeries('lowest', targetPrice.lowest),
    ],
  };

  return option;
};

const TargetPriceChart: React.FC<TargetPriceChartProps> = ({
  targetPrice,
  ratingHistory,
  height = TARGET_CHART_HEIGHT,
}) => {
  const option = buildTargetPriceChartOption(targetPrice, ratingHistory, height);

  return (
    <BaseEChart
      option={option}
      height={height}
      className="target-price-chart"
      ariaLabel="目標株価チャート（現在値、最高値、平均、最安値）"
      animate
    />
  );
};

export default TargetPriceChart;
